import { useHistory } from 'react-router'
import ReactTooltip from 'react-tooltip'
import BtnActionTable from '../../../components/BtnActionTable'
import Rules from '../../../components/Rules'
import useRule from '../../../hooks/useRule'
import Delete from '../../../sections/Delete'
import List from '../../../sections/List'
import { view } from '../../../sections/View'
import {
    getProductBrandName,
    getProductCode,
    getProductDescription,
    getProductManufacturerCode,
    getProductName,
    getProductSkuDisplay,
    getProductSubcategoryCategoryName,
    getProductSubcategoryName
} from '../../../utilities/productDisplay'
import { controlList } from '../storage'
import { ProductData, ProductListCompanyRow, ProductView } from '../types'
import Product from './Product'

function companyRowsForProductList(
    product: ProductData
): ProductListCompanyRow[] {
    if (product.companies_for_list && product.companies_for_list.length > 0) {
        return product.companies_for_list
    }
    return (product.product_countries ?? []).flatMap(pc =>
        (pc.product_companies ?? []).map(pcom => ({
            company: {
                name: pcom.company?.name,
                country: {
                    image_url: pcom.company?.country?.image_url
                }
            }
        }))
    )
}

function Code({ code, product_id }: { code: string; product_id: number }) {
    const math = useRule('CU071')

    return (
        <div
            {...(math && {
                className: 'underline cursor-pointer',
                onClick: () =>
                    view<ProductView>(
                        `/v5.6/apis/products/${product_id}`,
                        product => ({
                            title: getProductName(product),
                            subtitle: getProductCode(product),
                            Component: Product
                        })
                    )
            })}
        >
            <b>{code}</b>
        </div>
    )
}

export default function TableProducts() {
    const { push } = useHistory()
    return (
        <List control={controlList}>
            {(product, i) => (
                <tr key={product.id}>
                    <td>{i}</td>
                    <td>
                        <Code
                            code={getProductManufacturerCode(product)}
                            product_id={product.id}
                        />
                    </td>
                    <td style={{ whiteSpace: 'normal' }}>
                        <b>{getProductSkuDisplay(product)}</b>
                    </td>
                    <td className="max-w-md" style={{ whiteSpace: 'normal' }}>
                        {getProductDescription(product)}
                    </td>
                    <td className="max-w-sm">
                        <div className="flex" style={{ flexFlow: 'row wrap' }}>
                            {companyRowsForProductList(product).map(
                                (row, j) => (
                                    <div key={`${product.id}-co-${j}`}>
                                        <img
                                            data-for={`co-tip-${product.id}-${j}`}
                                            data-tip={row.company?.name}
                                            src={
                                                row.company?.country
                                                    ?.image_url ?? ''
                                            }
                                            alt=""
                                            className="mr-2 w-8 my-1"
                                            style={{ minWidth: '32px' }}
                                        />
                                        <ReactTooltip
                                            id={`co-tip-${product.id}-${j}`}
                                            place="top"
                                            type="dark"
                                            effect="solid"
                                            multiline={true}
                                        />
                                    </div>
                                )
                            )}
                        </div>
                    </td>
                    <td style={{ whiteSpace: 'normal' }}>
                        {getProductBrandName(product)}
                    </td>
                    <td style={{ whiteSpace: 'normal' }}>
                        {getProductSubcategoryCategoryName(product)}
                    </td>
                    <td style={{ whiteSpace: 'normal' }}>
                        {getProductSubcategoryName(product)}
                    </td>
                    <td>
                        <div className="group-btn-options">
                            <Rules cu="CU065">
                                <BtnActionTable
                                    title="Editar"
                                    iconName="bs-pencil"
                                    disabled={
                                        !getProductCode(product).startsWith(
                                            'NP-'
                                        )
                                    }
                                    onClick={() =>
                                        push(
                                            `/configuration/products/${product.id}/edition`
                                        )
                                    }
                                />
                            </Rules>
                            <Rules cu="CU066">
                                <Delete
                                    path={`/v5.6/apis/new_products/${product.id}`}
                                    control={controlList}
                                >
                                    ¿Está seguro de eliminar el producto{' '}
                                    <b className="text-primary">
                                        {getProductName(product)}
                                    </b>
                                    ?
                                </Delete>
                            </Rules>
                        </div>
                    </td>
                </tr>
            )}
        </List>
    )
}
